import i18n from "i18n";
import parse from "html-react-parser";
import React, { useCallback } from "react";
import { useQueryClient } from "react-query";
import { useHistory, useParams } from "react-router";
import { TFunction, Trans, useTranslation } from "react-i18next";

import {
  QuestionaireItem,
  useQuestionaireItems,
  useQuestionaireItemsForProject,
} from "pages/Backoffice/Questionaire/hooks";
import {
  Form as FormType,
  Certification as FormCertfication,
} from "@slootsantos/certain-forms/dist/types";
import {
  useAnnexSections,
  useAnnexSectionsForProject,
} from "pages/Backoffice/Questionaire/hooks/useAnnexSections";

import Form from "forms/Form";
import { useClient } from "utils/client";
import { Modal } from "components/Modal/Modal";
import { FeedbackContext } from "context/Feedback";
import { TrackingContext } from "context/Tracking";
import { Project } from "types";
import { Loading } from "components/Loading/Loading";
import { useTranslatedStandards } from "hooks/useStandards";
import Collapsible from "components/Collapsible/Collapsible";
import { PrimaryButton } from "components/Button/PrimaryButton";
import { AnnexSection, Directive } from "pages/Backoffice/Questionaire/types";
import { Category } from "components/common/ProductCategorySelector";
import { FeedbackLocation } from "components/Feedback/FeedbackPopup";
import { useProjectDetails } from "pages/Details/hooks/useProjectDetails";
import { ControlContext } from "context/Controls";
import {
  onlyUnique,
  uniqueArrayByProperty,
  filterStandardsWithVersions,
  buildAnnexFields,
  getExitQuestions,
  buildStandardFields,
  buildQuestionFields,
  getFirstLevelQuestions,
  getHighRiskQuestions,
} from "components/DirectiveSearch/util/baseHelpers";

// REMEMBER:
// There is primary and secondary directives.
// Primary directives are the ones driving the product category!
// E.g. MRL and LVD are primary. Hence they can **never** be together!
// Whereas the EMV is a secondary directive and can be combined with either MRL or LVD!
// With primary directives there is always one trumping the other!

interface DirectiveSearchRouteParams {
  projectId: string;
}

export const MvoResearch = () => {
  const client = useClient();
  const history = useHistory();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const langCode = i18n.language.split("-")[0].toUpperCase();
  const { directive = Directive.mrl } = useParams<{ directive: Directive }>();

  const { track } = React.useContext(TrackingContext);
  const { displayFeedback } = React.useContext(FeedbackContext);
  const { displayNotification } = React.useContext(ControlContext);

  const { projectId } = useParams<DirectiveSearchRouteParams>();
  const { project, fetchInProgress } = useProjectDetails(projectId);

  const { annexSections } = useAnnexSections();
  const [fd, setFd] = React.useState<any>(null);
  const [preloaded, setPreloaded] = React.useState(false);
  const [defaultData, setDefaultData] = React.useState<any>({});
  const questionaireItemsUnsorted = useQuestionaireItems();
  const [confirmedAnnexIV, setConfirmedAnnexIV] = React.useState(false);
  const [confirmedHighRisk, setConfirmedHighRisk] = React.useState(false);

  const {
    annexSections: annexSectionsProject,
    isLoading: annexSectionLoading,
  } = useAnnexSectionsForProject(projectId);

  const {
    questionaireItems: questionaireItemsProject,
    isLoading: questionaireLoading,
  } = useQuestionaireItemsForProject(projectId);

  const questionaireItems = questionaireItemsUnsorted?.sort((a, b) =>
    a.ref_number?.localeCompare(b.ref_number),
  );
  const standardsProject = project?.standards;
  const { translations } = useTranslatedStandards();

  const [isExitModalVisible, setIsExitModalVisible] = React.useState(false);
  const [exitModalContent, setExitModalContent] =
    React.useState<QuestionaireItem>();

  const [isHighRiskModalVisible, setHighRiskModalVisible] =
    React.useState(false);
  const [isCategoryModalVisible, setIsCategoryModalVisible] =
    React.useState(false);
  const [categoryModalContent, setCategoryModalContent] =
    React.useState<QuestionaireItem[]>();

  React.useEffect(() => {
    if (!questionaireItems?.length) return;

    // TEMP: Fallback to MRL right now until all projects have a directive
    const translationDirective =
      directive === undefined || directive === Directive._none
        ? "mrl"
        : directive;
    const exitQuestions = getExitQuestions(questionaireItems, directive);
    const formD: FormType = {
      type: FormCertfication.ce,
      documents: [],
      directives: [],
      sections: [
        {
          label: t(`form.${translationDirective}.exitQuestions.label`),
          name: "exitQuestions",
          info: t(`form.${translationDirective}.exitQuestions.info`),
          subline: t(`form.${translationDirective}.exitQuestions.subline`),
          fields: buildQuestionFields(exitQuestions, langCode),
          isGroup: false,
        },
        {
          label: t(`form.${translationDirective}.highRisk.label`),
          name: "highRisk",
          info: t(`form.${translationDirective}.highRisk.info`),
          subline: t(`form.${translationDirective}.highRisk.subline`),
          // isExclusive: true,
          fields: [],
        },
        {
          label: t(`form.${translationDirective}.category.label`),
          name: "category",
          info: t(`form.${translationDirective}.category.info`),
          subline: t(`form.${translationDirective}.category.subline`),
          // isExclusive: true,
          fields: [],
        },
        {
          label: t(`form.${translationDirective}.Anhang1.label`),
          name: "Anhang1",
          subline: t(""),
          info: t(`form.${translationDirective}.Anhang1.info`),
          fields: [],
        },
        {
          label: t(`form.${translationDirective}.Anhang1Relevante.label`),
          name: "Anhang1Relevante",
          subline: t(""),
          info: t(`form.${translationDirective}.Anhang1Relevante.info`),
          fields: [],
        },
        {
          label: t(`form.${translationDirective}.Normenauswahl.label`),
          name: "Normenauswahl",
          info: t(`form.${translationDirective}.Normenauswahl.info`),
          subline: t(`form.${translationDirective}.Normenauswahl.subline`),
          fields: [],
        },
      ],
    };

    setFd(formD);
  }, [questionaireItems, langCode, t, directive]);

  const handleSave = React.useCallback(
    async (form: any, isManualSubmit?: boolean) => {
      const annexIvCategoryQuestionaireId = questionaireItems.find(
        ({ product_category }) => product_category === "annex_iv",
      )?.resource_id;

      const categoryResponses = form.category
        ? Object.keys(form.category)
            .filter((categoryKey) => form?.category[categoryKey] !== "")
            .map((categoryKey) => {
              const id = categoryKey.split("field")[0];

              return {
                id,
                isSelected: Boolean(parseInt(form.category[categoryKey])),
              };
            })
        : [];

      const allSelectedCategories = categoryResponses.filter(
        ({ isSelected }) => isSelected,
      );

      const categoryResponse = allSelectedCategories
        .filter(
          ({ id }) =>
            id !== annexIvCategoryQuestionaireId ||
            allSelectedCategories.length === 1,
        )
        .find(Boolean);

      const selectedCategory =
        questionaireItems.find(
          ({ resource_id }) => resource_id === categoryResponse?.id,
        )?.product_category || Category.none;

      const s = form.Normenauswahl
        ? Object.keys(form.Normenauswahl)
            .filter((norm) => Boolean(parseInt(form.Normenauswahl[norm])))
            .map((n) => {
              const completeStandards = translations?.find(
                (tr) => tr.name === n,
              );

              return [n, completeStandards?.latest].join(":");
            })
        : [];

      const uniqueStds = filterStandardsWithVersions([
        ...(standardsProject || []),
        ...s,
      ]);

      const isHighRisk = Object.values(form.highRisk).some(
        (value) => value === "1",
      );

      await client.put(`/projects/${projectId}`, {
        payload: {
          ...project,
          notifiedBody: {
            ...project?.notifiedBody,
            required: isHighRisk,
          },
          standards: uniqueStds,
          directives: project!.directives.map((d) => {
            if (d.name === directive) {
              return {
                ...d,
                is_exit: false,
                completed_research: true,
              };
            }

            return d;
          }),
          projectdata: {
            ...project!.projectdata,
            category: selectedCategory,
            ...(isManualSubmit ? { completed_toplevel_research: true } : {}),
          },
        } as Project,
      });

      const exitResponses = form?.exitQuestions
        ? Object.keys(form.exitQuestions)
            .filter((exitKey) => form?.exitQuestions[exitKey] !== "")
            .map((exitKey) => {
              const id = exitKey.split("field")[0];
              return {
                id,
                isSelected: Boolean(parseInt(form.exitQuestions[exitKey])),
              };
            })
        : [];

      const highRiskResponses = form.highRisk
        ? Object.keys(form.highRisk)
            .filter((riskKey) => form?.highRisk[riskKey] !== "")
            .map((riskKey) => {
              const id = riskKey.split("field")[0];
              return {
                id,
                isSelected: Boolean(parseInt(form.highRisk[riskKey])),
              };
            })
        : [];

      const anhangResponses = form.Anhang1
        ? Object.keys(form.Anhang1)
            .filter((anhangKey) => form?.Anhang1[anhangKey] !== "")
            .map((anhangKey) => {
              const id = anhangKey.split("field")[0];
              return {
                id,
                isSelected: Boolean(parseInt(form.Anhang1[anhangKey])),
              };
            })
        : [];

      const questionaireIds = [
        // Keep the order of the questions based on their order in the form!!
        ...exitResponses,
        ...highRiskResponses,
        ...categoryResponses,
        ...anhangResponses,
      ].map(({ id, isSelected, ...r }) => ({
        questionaireId: id,
        isSelected: isSelected,
      }));

      const annexIds = form.Anhang1Relevante
        ? Object.keys(form.Anhang1Relevante)
            .map((anhangKey) => {
              const id = anhangKey.split("field")[0];
              return {
                id,
                isSelected: Boolean(parseInt(form.Anhang1Relevante[anhangKey])),
              };
            })
            .map(({ id, isSelected }) => ({ annexId: id, isSelected }))
        : [];

      const uniqueQuestionaires = uniqueArrayByProperty(
        [
          ...questionaireIds,
          ...(questionaireItemsProject?.questionaires || []),
        ],
        "questionaireId",
      );

      await client.post(
        `/projects/${projectId}/questionaireItems`,
        uniqueQuestionaires,
      );
      await client.post(`/projects/${projectId}/annexSections`, annexIds);

      queryClient.refetchQueries(["projects", projectId]);
      queryClient.refetchQueries(["questionaireItems", projectId]);
      queryClient.refetchQueries(["annexSections", projectId]);

      displayNotification!(
        "Erfolgreich gespeichert!",
        "Ihre Recherchefortschritt wurde gespeichert",
      );
    },
    [
      client,
      project,
      projectId,
      queryClient,
      translations,
      questionaireItems,
      displayNotification,
      questionaireItemsProject,
    ],
  );

  const handleSubmit = useCallback(
    async (form: any) => {
      await handleSave(form, true);

      track("finish questionaire");

      displayFeedback!(FeedbackLocation.Questionaire);
      history.push(`/projects/${projectId}?saved=true`);
    },
    [displayFeedback, history, projectId, track, handleSave],
  );

  const handleStepCallback = React.useCallback(
    (v: any, currentStep: number, _: any, isPreload?: boolean) => {
      // ******************     EXIT / Step 1       **************************
      if (!fd?.sections?.length || !translations?.length || !v.exitQuestions)
        return;
      const shouldExit = Object.values(v.exitQuestions).some(
        (value) => value === "1",
      );

      const exitQuestion = Object.keys(v.exitQuestions)
        ?.filter((key) => v.exitQuestions[key] === "1")
        ?.map((key) => key.split("field")[0])
        ?.map((keyId) =>
          questionaireItems.find(({ resource_id }) => resource_id === keyId),
        );

      if (shouldExit) {
        const preventNextStepFromCallback = true;

        setIsExitModalVisible(true);
        setExitModalContent(exitQuestion[0]);

        return preventNextStepFromCallback;
      }
      // ******************     HIGH RISK / Step 2       **************************
      fd.sections[1].fields = [
        ...buildQuestionFields(
          getHighRiskQuestions(questionaireItems, directive),
          langCode,
        ),
      ];

      if (!v.highRisk) {
        return;
      }

      const isHighRisk = Object.values(v.highRisk).some(
        (value) => value === "1",
      );

      if (isHighRisk && !isPreload && currentStep === 1 && !confirmedHighRisk) {
        const preventNextStepFromCallback = true;
        setHighRiskModalVisible(true);

        return preventNextStepFromCallback;
      }

      // ******************     CATEGORY / Step 3       **************************
      fd.sections[2].fields = [
        ...buildQuestionFields(
          getFirstLevelQuestions(questionaireItems, directive),
          langCode,
        ),
      ];

      // ******************     PRE_SELECT / Step 4       **************************

      if (!v.category) {
        return;
      }

      const appliedIds = Object.keys(v.category).filter((qId) =>
        parseInt(v.category[qId]),
      );

      const appliedQuestions = questionaireItems.filter((q) =>
        appliedIds.includes(q.resource_id!.toString() + "field"),
      );

      const followupIds = appliedQuestions.flatMap((aq) => aq.followups);
      const followups = followupIds
        .filter(onlyUnique)
        .map((id) => questionaireItems.find((qi) => qi.resource_id === id)!)
        .sort((a, b) => a.ref_number.localeCompare(b.ref_number))
        .filter(Boolean);

      fd.sections[3].fields = [...buildQuestionFields(followups, langCode)];

      const selectedExactlyOne = appliedQuestions.length === 1;
      const isAnnexIVSelected = appliedQuestions.find(
        ({ product_category }) => product_category === "annex_iv",
      );

      const shouldPopUpForCategory = !selectedExactlyOne || isAnnexIVSelected;

      if (
        !isPreload &&
        currentStep === 2 &&
        shouldPopUpForCategory &&
        !confirmedAnnexIV
      ) {
        const preventNextStepFromCallback = true;

        setIsCategoryModalVisible(true);
        setCategoryModalContent(appliedQuestions);

        return preventNextStepFromCallback;
      }

      // ******************     Annex / Step 5       **************************
      if (!v.Anhang1) {
        return;
      }

      const appliedIdsForAnnex = Object.keys(v.Anhang1).filter((qId) =>
        parseInt(v.Anhang1[qId]),
      );

      const appliedAnnex = questionaireItems.filter((q) =>
        appliedIdsForAnnex.includes(q.resource_id!.toString() + "field"),
      );

      const annexIds = appliedAnnex.flatMap((aq) => aq.annex_sections);
      const annexes = annexIds
        .filter(onlyUnique)
        .map((id) => annexSections.find((qi) => qi.resource_id === id)!)
        .filter(Boolean);

      const isCorrectDirectiveForAnnex = (annex: AnnexSection) =>
        annex.directive === Directive.mvo;

      const requiredAnnex = annexSections.filter(
        (qi) =>
          (isCorrectDirectiveForAnnex(qi) && qi.name.match(/^1\./)) ||
          qi.name.match(/^0\./),
      )!;

      const allSortedAnnexes = [...requiredAnnex, ...annexes].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true }),
      )!;

      const _groups = allSortedAnnexes.filter(
        (annex) =>
          isCorrectDirectiveForAnnex(annex) &&
          annex.is_informational &&
          annex.name.length < 3, // matches "1." or "2."
      );

      const _noGroup = allSortedAnnexes.filter(
        (annex) =>
          isCorrectDirectiveForAnnex(annex) &&
          (!annex.is_informational ||
            (annex.is_informational && annex.name.length >= 3)), // matches "1.1." or "2.1."
      );

      fd.sections[4] = {
        ...fd.sections[4],
        fields: [...buildAnnexFields(_noGroup, langCode, _groups, t)],
        isGroup: true,
        groups: _groups.reverse().map((group) => {
          return {
            name: group.name,
            label: `${group.name} ${
              group.questions.find((q) => q.language === langCode)?.content
            }`,
          };
        }),
      };

      !isPreload &&
        currentStep === 3 &&
        displayFeedback!(FeedbackLocation.Questionaire3);

      if (!v.Anhang1Relevante) {
        return;
      }

      const appliedStandardIds = Object.keys(v.Anhang1Relevante).filter((qId) =>
        parseInt(v.Anhang1Relevante[qId]),
      );

      const appliedStandards = annexSections
        .filter((q) =>
          appliedStandardIds.includes(q.resource_id!.toString() + "field"),
        )
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { numeric: true }),
        );

      const alreadyUsedStandards: string[] = [];
      const standardGroups = appliedStandards.flatMap((aq) => {
        const filteredStandards =
          aq.suggested_standards
            ?.filter(onlyUnique)
            ?.filter((std) => !alreadyUsedStandards.includes(std))
            ?.map(
              (name) =>
                translations!.find((s) => s.name === name.split(":")[0])!,
            )
            .filter(Boolean) || [];

        alreadyUsedStandards.push(...filteredStandards.map((std) => std.name));

        return {
          standards: filteredStandards,
          relatedSection: aq,
        };
      });

      !isPreload &&
        currentStep === 4 &&
        displayFeedback!(FeedbackLocation.Questionaire4);

      fd.sections[5].fields = [...buildStandardFields(standardGroups)];
      fd.sections[5].isGroup = true;
      fd.sections[5].groups = standardGroups
        .filter((sg) => sg.standards.length)
        .map(({ relatedSection }) => ({
          name: relatedSection.name,
          label: relatedSection.questions.find((q) => q.language === langCode)!
            .content,
        }));

      const n = { ...fd };
      setFd(n);
    },
    [
      t,
      fd,
      langCode,
      directive,
      translations,
      annexSections,
      displayFeedback,
      confirmedAnnexIV,
      confirmedHighRisk,
      questionaireItems,
    ],
  );

  React.useEffect(() => {
    if (
      !questionaireItems?.length ||
      !questionaireItemsProject ||
      !annexSectionsProject ||
      fetchInProgress
    ) {
      return;
    }

    const rawExitQuestions = getExitQuestions(questionaireItems, directive);
    const exitQuestionIds = rawExitQuestions.map((q) => q.resource_id!);

    const rawFirstQuestions = getFirstLevelQuestions(
      questionaireItems,
      directive,
    );
    const firstQuestionIds = rawFirstQuestions.map((q) => q.resource_id!);

    const rawHighRiskQuestions = getHighRiskQuestions(
      questionaireItems,
      directive,
    );
    const highRiskIds = rawHighRiskQuestions.map((q) => q.resource_id!);

    const exitQuestions = questionaireItemsProject?.questionaires
      ?.filter((q) => exitQuestionIds.includes(q.questionaireId))
      .reduce(
        (acc, key) => {
          acc[key.questionaireId.toString() + "field"] = key.isSelected
            ? "1"
            : "0";

          return acc;
        },
        {} as Record<string, string>,
      );

    const category = questionaireItemsProject?.questionaires
      ?.filter((q) => firstQuestionIds.includes(q.questionaireId))
      .reduce(
        (acc, key) => {
          acc[key.questionaireId.toString() + "field"] = key.isSelected
            ? "1"
            : "0";

          return acc;
        },
        {} as Record<string, string>,
      );

    const highRisk = questionaireItemsProject?.questionaires
      ?.filter((q) => highRiskIds.includes(q.questionaireId))
      .reduce(
        (acc, key) => {
          acc[key.questionaireId.toString() + "field"] = key.isSelected
            ? "1"
            : "0";

          return acc;
        },
        {} as Record<string, string>,
      );

    const anhang1 = questionaireItemsProject?.questionaires
      ?.filter((q) => !firstQuestionIds.includes(q.questionaireId))
      .reduce(
        (acc, key) => {
          acc[key.questionaireId.toString() + "field"] = key.isSelected
            ? "1"
            : "0";

          return acc;
        },
        {} as Record<string, string>,
      );

    const annex = annexSectionsProject?.annexSections?.reduce(
      (acc, key) => {
        acc[key.annexId.toString() + "field"] = key.isSelected ? "1" : "0";

        return acc;
      },
      {} as Record<string, string>,
    );

    const norm = standardsProject?.reduce(
      (acc, key) => {
        const stdWithoutVersion = key.split(":")[0];
        acc[stdWithoutVersion] = "1";

        return acc;
      },
      {} as Record<string, string>,
    );

    const defaultData = {
      exitQuestions,
      category,
      highRisk,
      Anhang1: anhang1,
      Anhang1Relevante: annex,
      Normenauswahl: norm,
    };

    setDefaultData(defaultData);

    if (
      defaultData &&
      !preloaded &&
      translations?.length &&
      fd?.sections.length
    ) {
      handleStepCallback(defaultData, 0, null, true);
      setPreloaded(true);
    }
  }, [
    fd,
    preloaded,
    directive,
    translations,
    fetchInProgress,
    standardsProject,
    questionaireItems,
    handleStepCallback,
    annexSectionsProject,
    questionaireItemsProject,
  ]);

  if (
    !fd?.sections ||
    questionaireLoading ||
    annexSectionLoading ||
    (questionaireItemsProject?.questionaires && !defaultData.exitQuestions) ||
    (annexSectionsProject?.annexSections &&
      (!defaultData.Anhang1Relevante ||
        !Object.keys(defaultData.Anhang1Relevante).length))
  ) {
    return <Loading />;
  }

  return (
    <div className="questionaire">
      <Form
        persistStep
        formData={fd}
        onSubmit={handleSubmit}
        onAutosave={handleSave}
        defaultValues={defaultData}
        onStep={handleStepCallback}
        disableStepper={!defaultData.exitQuestions || !defaultData.category}
      />

      <Modal
        onClose={() => setIsExitModalVisible(false)}
        show={isExitModalVisible}
        width="1/2"
        content={
          <div>
            <span className="text-xl">
              {t(`questionaire.${directive}.exit_modal.headline`)}
            </span>
            <div className="pt-4"></div>
            <div>
              <p>{t(`questionaire.${directive}.exit_modal.subline`)}</p>
              <Collapsible
                label={`${t(
                  `questionaire.${directive}.exit_modal.collapsible_label`,
                )} ${exitModalContent?.article}`}
                child={
                  <>
                    <p>
                      <b>
                        "
                        {
                          exitModalContent?.questions?.find(
                            (t) => t.language === langCode,
                          )?.content
                        }
                        "
                      </b>
                    </p>
                    {parse(
                      exitModalContent?.originalTexts1?.find(
                        (t) => t.language === langCode,
                      )?.content || "",
                    )}
                  </>
                }
              />
              <p className="pt-4 pb-4">
                {t(`questionaire.${directive}.exit_modal.explanation`)}
              </p>

              <div className="pt-9 text-right">
                <a
                  className="px-6 text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
                  onClick={() => setIsExitModalVisible(false)}
                >
                  {t(`questionaire.${directive}.exit_modal.cancel`)}
                </a>
                <PrimaryButton
                  onClick={async () => {
                    await client.put(`/projects/${projectId}`, {
                      payload: {
                        ...project,
                        directives: project!.directives.map((d) => {
                          if (d.name === directive) {
                            return {
                              ...d,
                              is_exit: true,
                              completed_research: true,
                            };
                          }

                          return d;
                        }),
                        projectdata: {
                          ...project!.projectdata,
                          category: Category.exit,
                        },
                      } as Project,
                    });

                    await client.post(
                      `/projects/${projectId}/questionaireItems`,
                      [
                        ...(questionaireItemsProject?.questionaires || []),
                        {
                          questionaireId: exitModalContent?.resource_id,
                          isSelected: true,
                        },
                      ],
                    );

                    await queryClient.refetchQueries(["projects", projectId]);
                    await queryClient.invalidateQueries([
                      "questionaireItems",
                      projectId,
                    ]);

                    history.push(`/projects/${projectId}?saved=true`);
                  }}
                >
                  {t(`questionaire.${directive}.exit_modal.cta`)}
                </PrimaryButton>
              </div>
            </div>
          </div>
        }
      />
      {/* -------------------------------------------------------------- */}

      {isHighRiskModalVisible && (
        <HighRiskModal
          t={t}
          onConfirm={() => {
            setHighRiskModalVisible(false);
            setConfirmedHighRisk(true);
          }}
          isHighRiskModalVisible={isHighRiskModalVisible}
          setHighRiskModalVisible={setHighRiskModalVisible}
        />
      )}

      {isCategoryModalVisible &&
        (!categoryModalContent?.length || categoryModalContent?.length < 1) && (
          <NoCategoryModal
            t={t}
            isCategoryModalVisible={isCategoryModalVisible}
            setIsCategoryModalVisible={setIsCategoryModalVisible}
          />
        )}

      {isCategoryModalVisible &&
        categoryModalContent?.length! >= 2 &&
        (!categoryModalContent?.find(
          (item) => item.product_category === "annex_iv",
        ) ||
          categoryModalContent?.length! > 2) && (
          <TooManyCategoryModal
            t={t}
            langCode={langCode}
            directive={directive}
            isCategoryModalVisible={isCategoryModalVisible}
            categoryModalContent={categoryModalContent || []}
            setIsCategoryModalVisible={setIsCategoryModalVisible}
          />
        )}

      {isCategoryModalVisible &&
        categoryModalContent?.length &&
        categoryModalContent.length <= 2 &&
        categoryModalContent?.find(
          (item) => item.product_category === "annex_iv",
        ) && (
          <CategoryAnnexIVModal
            onConfirm={() => {
              setIsCategoryModalVisible(false);
              setConfirmedAnnexIV(true);
            }}
            t={t}
            directive={directive}
            isCategoryModalVisible={isCategoryModalVisible}
            setIsCategoryModalVisible={setIsCategoryModalVisible}
          />
        )}
    </div>
  );
};

interface TooManyCategoryModalProps {
  directive: Directive;
  isCategoryModalVisible: boolean;
  setIsCategoryModalVisible: (isVisible: boolean) => void;
  categoryModalContent: QuestionaireItem[];
  langCode: string;
  t: TFunction;
}

const TooManyCategoryModal = ({
  setIsCategoryModalVisible,
  isCategoryModalVisible,
  directive,
  categoryModalContent,
  langCode,
  t,
}: TooManyCategoryModalProps) => {
  return (
    <Modal
      onClose={() => setIsCategoryModalVisible(false)}
      show={isCategoryModalVisible}
      width="1/2"
      content={
        <div>
          <span className="text-xl">
            {t(`questionaire.${directive}.category_modal.headline`)}
          </span>
          <div className="pt-4"></div>
          <div>
            <p>
              <Trans
                i18nKey={`questionaire.${directive}.category_modal.subline`}
              />
            </p>

            <p>
              <Trans
                i18nKey={`questionaire.${directive}.category_modal.explanation`}
                count={categoryModalContent?.length}
              />
            </p>

            {categoryModalContent?.map((category) => {
              return (
                <Collapsible
                  key={category.article}
                  label={t(
                    `common.selector.product_category.${category?.product_category}`,
                  )}
                  child={
                    <>
                      <p>
                        <b>
                          {t("common.question")}: "
                          {parse(
                            category?.questions?.find(
                              (t) => t.language === langCode,
                            )?.content || "",
                          )}
                          "
                        </b>
                      </p>
                      {parse(
                        category?.explanations?.find(
                          (t) => t.language === langCode,
                        )?.content || "",
                      )}
                    </>
                  }
                />
              );
            })}

            <div className="pt-9 text-right">
              <PrimaryButton onClick={() => setIsCategoryModalVisible(false)}>
                {t(`questionaire.${directive}.category_modal.cta`)}
              </PrimaryButton>
            </div>
          </div>
        </div>
      }
    />
  );
};

interface CategoryAnnexIVModalProps {
  directive: Directive;
  isCategoryModalVisible: boolean;
  setIsCategoryModalVisible: (isVisible: boolean) => void;
  onConfirm: () => void;
  t: TFunction;
}

const CategoryAnnexIVModal = ({
  setIsCategoryModalVisible,
  isCategoryModalVisible,
  directive,
  onConfirm,
  t,
}: CategoryAnnexIVModalProps) => {
  return (
    <Modal
      onClose={() => setIsCategoryModalVisible(false)}
      show={isCategoryModalVisible}
      width="1/2"
      content={
        <div>
          <span className="text-xl">
            {t(`questionaire.${directive}.annex_iv_modal.headline`)}
          </span>
          <div className="pt-4"></div>
          <div>
            <p>
              <Trans
                i18nKey={`questionaire.${directive}.annex_iv_modal.subline`}
              >
                <a
                  target="_blank"
                  className="text-cyan-600 cursor-pointer hover:text-cyan-800"
                  href="https://webgate.ec.europa.eu/single-market-compliance-space/#/notified-bodies/by-country"
                ></a>
              </Trans>
            </p>

            <div className="pt-9 text-right">
              <PrimaryButton onClick={onConfirm}>
                {t(`questionaire.${directive}.annex_iv_modal.cta`)}
              </PrimaryButton>
            </div>
          </div>
        </div>
      }
    />
  );
};

interface NoCategoryModalProps {
  isCategoryModalVisible: boolean;
  setIsCategoryModalVisible: (isVisible: boolean) => void;
  t: TFunction;
}

const NoCategoryModal = ({
  setIsCategoryModalVisible,
  isCategoryModalVisible,
  t,
}: NoCategoryModalProps) => {
  return (
    <Modal
      onClose={() => setIsCategoryModalVisible(false)}
      show={isCategoryModalVisible}
      width="1/2"
      content={
        <div>
          <span className="text-xl">
            {t(`questionaire.no_category_modal.headline`)}
          </span>
          <div className="pt-4"></div>
          <div>
            <p>
              <Trans i18nKey={`questionaire.no_category_modal.subline`}>
                <a
                  target="_blank"
                  className="text-cyan-600 cursor-pointer hover:text-cyan-800"
                  href="https://webgate.ec.europa.eu/single-market-compliance-space/#/notified-bodies/by-country"
                ></a>
              </Trans>
            </p>

            <div className="pt-9 text-right">
              <PrimaryButton onClick={() => setIsCategoryModalVisible(false)}>
                {t(`questionaire.no_category_modal.cta`)}
              </PrimaryButton>
            </div>
          </div>
        </div>
      }
    />
  );
};

interface HighRiskQuestionModalProps {
  isHighRiskModalVisible: boolean;
  onConfirm: () => void;
  setHighRiskModalVisible: (isVisible: boolean) => void;
  t: TFunction;
}

const HighRiskModal = ({
  onConfirm,
  setHighRiskModalVisible,
  isHighRiskModalVisible,
  t,
}: HighRiskQuestionModalProps) => {
  return (
    <Modal
      onClose={() => setHighRiskModalVisible(false)}
      show={isHighRiskModalVisible}
      width="1/2"
      content={
        <div>
          <span className="text-xl">
            <Trans i18nKey={`questionaire.mvo.high_risk.headline`} />
          </span>
          <div className="pt-4"></div>
          <div>
            <p>
              <Trans i18nKey={`questionaire.mvo.high_risk.explanation`}></Trans>
            </p>

            <div className="pt-9 text-right">
              <PrimaryButton onClick={onConfirm}>
                {t(`questionaire.mvo.high_risk.cta`)}
              </PrimaryButton>
            </div>
          </div>
        </div>
      }
    />
  );
};
